import type {
  Modification,
  ModificationGroupLimits,
  ModificationsMap,
  PairedModifierGroupMap,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Optimizes which modifications sit in included vs extras slots to ensure
 * the customer always gets the cheapest arrangement.
 *
 * Two strategies, applied in order for each active extra:
 *
 * 1. **Downgrade** — if the paired included group has a free slot, move the
 *    extra into it (removing the upcharge).
 * 2. **Swap** — if the group is full but contains an occupant that was not
 *    originally included (a "downgraded guest"), swap it back to extras and
 *    reclaim the included slot for the item that was originally there.
 */
export function optimizeIncludedSlots(params: Params): Modification[] {
  const {
    activeModifications,
    initialModifications,
    includedModificationsMap,
    extrasModificationsMap,
    includedModifierGroupsByExtrasName,
    modificationLimits,
  } = params;

  const capacity = buildIncludedGroupCapacity({
    activeModifications,
    includedModifierGroupsByExtrasName,
    modificationLimits,
  });

  if (capacity.size === 0) return activeModifications;

  const initialIncludedIngredientIds = buildInitialIncludedIngredientIds(initialModifications);

  const plan = buildOptimizationPlan({
    activeModifications,
    includedModificationsMap,
    extrasModificationsMap,
    capacity,
    initialIncludedIngredientIds,
  });

  if (plan.size === 0) return activeModifications;

  const withPlan = activeModifications.flatMap((modification, index) => {
    return plan.get(index) ?? [modification];
  });

  return mergeDuplicates(withPlan);
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildOptimizationPlan(params: OptimizationPlanParams) {
  const {
    activeModifications,
    includedModificationsMap,
    extrasModificationsMap,
    capacity,
    initialIncludedIngredientIds,
  } = params;

  const reservedOccupants = new Set<number>();

  return activeModifications.reduce<Map<number, Modification[]>>((plan, extra, extraIndex) => {
    if (!extra.isExtra || !extra.ingredientId) return plan;

    const includedCounterpart = includedModificationsMap.get(extra.ingredientId);

    if (!includedCounterpart) return plan;

    const includedGroupName = includedCounterpart.modificationGroupName;
    const available = capacity.get(includedGroupName);

    if (available === undefined) return plan;

    if (available > 0) {
      const quantityToDowngrade = Math.min(extra.quantity, available);

      capacity.set(includedGroupName, available - quantityToDowngrade);
      plan.set(extraIndex, buildReplacement(extra, includedCounterpart, quantityToDowngrade));

      return plan;
    }

    if (!initialIncludedIngredientIds.has(extra.ingredientId)) return plan;

    const occupantIndex = findDemotableOccupant({
      activeModifications,
      extrasModificationsMap,
      includedGroupName,
      reservedOccupants,
      initialIncludedIngredientIds,
    });

    if (occupantIndex === -1) return plan;

    const occupant = activeModifications[occupantIndex];
    const extrasCounterpart = extrasModificationsMap.get(occupant.ingredientId!);

    if (!extrasCounterpart) return plan;

    const quantityToSwap = Math.min(extra.quantity, occupant.quantity);

    plan.set(extraIndex, buildReplacement(extra, includedCounterpart, quantityToSwap));
    plan.set(occupantIndex, buildReplacement(occupant, extrasCounterpart, quantityToSwap));

    reservedOccupants.add(occupantIndex);

    return plan;
  }, new Map());
}

// ─────────────────────────────────────────────────────────────────────────────

function buildIncludedGroupCapacity(params: CapacityParams) {
  const { activeModifications, includedModifierGroupsByExtrasName, modificationLimits } = params;

  const activeCounts = activeModifications.reduce<GroupCountsByName>(accumulateGroupCounts, {});
  const computeCapacity = computeCapacityFactory({ activeCounts, modificationLimits });

  return Object.values(includedModifierGroupsByExtrasName).reduce(computeCapacity, new Map());
}

function accumulateGroupCounts(counts: GroupCountsByName, modification: Modification) {
  const { modificationGroupName, quantity } = modification;

  return {
    ...counts,
    [modificationGroupName]: (counts[modificationGroupName] ?? 0) + quantity,
  };
}

function computeCapacityFactory(params: ComputeCapacityFactoryParams) {
  const { activeCounts, modificationLimits } = params;

  return function (capacity: GroupCapacityByName, includedGroupName: string) {
    if (capacity.has(includedGroupName)) return capacity;

    const max = modificationLimits[includedGroupName]?.maxAggregateQuantity;

    if (max === undefined) return capacity;

    const available = Math.max(0, max - (activeCounts[includedGroupName] ?? 0));

    capacity.set(includedGroupName, available);

    return capacity;
  };
}

// ─────────────────────────────────────────────────────────────────────────────

function buildInitialIncludedIngredientIds(initialModifications: Modification[]) {
  return initialModifications.reduce<Set<string>>((ids, modification) => {
    if (!modification.isExtra && modification.ingredientId) {
      ids.add(modification.ingredientId);
    }

    return ids;
  }, new Set());
}

function findDemotableOccupant(params: FindOccupantParams): number {
  const {
    activeModifications,
    extrasModificationsMap,
    includedGroupName,
    reservedOccupants,
    initialIncludedIngredientIds,
  } = params;

  return activeModifications.findIndex((modification, index) => {
    if (reservedOccupants.has(index)) return false;
    if (modification.modificationGroupName !== includedGroupName) return false;
    if (modification.isExtra) return false;
    if (!modification.ingredientId) return false;
    if (!extrasModificationsMap.has(modification.ingredientId)) return false;

    // Only demote items that were not originally included — they are downgraded guests.
    return !initialIncludedIngredientIds.has(modification.ingredientId);
  });
}

function buildReplacement(
  original: Modification,
  counterpart: Modification,
  quantityToChange: number,
): Modification[] {
  const swapped = withQuantity(counterpart, quantityToChange);
  const remaining = original.quantity - quantityToChange;

  if (remaining > 0) {
    return [swapped, withQuantity(original, remaining)];
  }

  return [swapped];
}

function withQuantity(modification: Modification, quantity: number): Modification {
  return { ...modification, quantity };
}

/**
 * After the plan is applied a downgrade/swap can leave two entries with the
 * same modifier id (e.g. the active list already held an included counterpart
 * and the optimizer promoted an extra into the same slot). Collapse those
 * into a single entry by summing quantities.
 */
function mergeDuplicates(modifications: Modification[]): Modification[] {
  const byId = modifications.reduce<Map<string, Modification>>((merged, modification) => {
    const existing = merged.get(modification.id);
    const updatedModification = existing
      ? withQuantity(existing, existing.quantity + modification.quantity)
      : modification;

    return merged.set(modification.id, updatedModification);
  }, new Map());

  return [...byId.values()];
}

// ─── Types ──────────────────────────────────────────────────────────────────

type Params = {
  activeModifications: Modification[];
  initialModifications: Modification[];
  includedModificationsMap: ModificationsMap;
  extrasModificationsMap: ModificationsMap;
  includedModifierGroupsByExtrasName: PairedModifierGroupMap;
  modificationLimits: ModificationGroupLimits;
};

type CapacityParams = {
  activeModifications: Modification[];
  includedModifierGroupsByExtrasName: PairedModifierGroupMap;
  modificationLimits: ModificationGroupLimits;
};

type GroupCountsByName = Record<string, number>;

type GroupCapacityByName = Map<string, number>;

type ComputeCapacityFactoryParams = {
  activeCounts: GroupCountsByName;
  modificationLimits: ModificationGroupLimits;
};

type OptimizationPlanParams = {
  activeModifications: Modification[];
  includedModificationsMap: ModificationsMap;
  extrasModificationsMap: ModificationsMap;
  capacity: GroupCapacityByName;
  initialIncludedIngredientIds: Set<string>;
};

type FindOccupantParams = {
  activeModifications: Modification[];
  extrasModificationsMap: ModificationsMap;
  includedGroupName: string;
  reservedOccupants: Set<number>;
  initialIncludedIngredientIds: Set<string>;
};
